import { computed, Signal } from '@angular/core';
import { Event } from '../../events/models';
import { formatDate } from 'date-fns';
import { SocialChannel } from '../../social-media/models';
import { Button } from '@teamfoster/sdk/button';
import { StripHtmlPipe, StripHtmlTagsPipe } from '@teamfoster/sdk/text-utility';

export const StructuredData = {
  /**
   * @see https://schema.org/Event
   * @param data - a Signal containing an Event
   * @returns Structured Data JSON for an event
   */
  fromEvent(data: Signal<Event | null>) {
    return computed(() => {
      const event = data();

      if (!event) {
        return {};
      }

      const schema: any = {
        '@context': 'https://schema.org',
        '@type': 'Event',
        'name': `${event?.title}`,
        'image': `${event?.image?.absoluteUrl}`,
        'description': `${new StripHtmlTagsPipe().transform(event?.description)}`,
        'eventStatus': 'https://schema.org/EventScheduled',
        'eventAttendanceMode': 'https://schema.org/OfflineEventAttendanceMode',
        'organizer': 'CODA Apeldoorn',
        'location':
          event?.locations?.[0] !== undefined
            ? {
                '@type': 'Place',
                'name': `${event?.locations?.[0].name}`,
                'geo': {
                  '@type': 'GeoCoordinates',
                  'latitude': `${event?.locations?.[0].geoLocation?.lat}`,
                  'longitude': `${event?.locations?.[0].geoLocation?.lng}`,
                },
              }
            : {
                '@type': 'Place',
                'name': `CODA Apeldoorn`,
                'address': {
                  '@type': 'PostalAddress',
                  'streetAddress': 'Vosselmanstraat 299',
                  'addressLocality': 'Apeldoorn',
                  'postalCode': '7311 CL',
                  'addressCountry': 'NL',
                },
              },
      };

      if (event.startDate) {
        let startDate = formatDate(new Date(event.startDate), 'yyyy-MM-dd');
        if (event.displayEventStartTime) {
          startDate += 'T' + formatDate(new Date(event.startDate), 'HH:mm:ss');
        }

        schema['startDate'] = startDate;
      }

      if (event.endDate) {
        let endDate = formatDate(new Date(event.endDate), 'yyyy-MM-dd');
        if (event.displayEventEndTime) {
          endDate += 'T' + formatDate(new Date(event.endDate), 'HH:mm:ss');
        }
        schema['endDate'] = endDate;
      }

      return schema;
    });
  },

  /**
   * @see https://schema.org/BreadcrumbList
   * @param data - a Signal containing an array of Button
   * @returns Structured Data JSON for a breadcrumb list
   */
  fromBreadcrumb(data: Signal<Button[] | undefined>) {
    return computed(() => {
      if (!data()) {
        return {};
      }

      const items = data()?.map((item, index) => {
        // If the item has a URL, use it; otherwise, construct the URL from the routerLink. remove each leading slash of the routerLink parts to avoid double slashes.
        // Add query params if any, and add a fragment if any
        let url =
          item.url ||
          `https://www.coda-apeldoorn.nl/${
            item.routerLink
              ?.filter(a => a !== '/')
              .map(a => a.replace(/^\/+/, ''))
              .join('/') || ''
          }`;
        if (item.queryParams) {
          const queryString = new URLSearchParams(item.queryParams).toString();
          url += `?${queryString}`;
        }
        if (item.fragment) {
          url += `#${item.fragment}`;
        }

        return {
          '@type': 'ListItem',
          'position': index + 1,
          'name': item.text,
          'item': url,
        };
      });

      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        'itemListElement': items,
      };
    });
  },

  /**
   * @see https://schema.org/Organization
   * @param data - a Signal containing an array of SocialChannel items
   * @returns Structured Data JSON for an organization
   */
  organization(data: Signal<SocialChannel[]>) {
    return computed(() => {
      const channels = data().map(channel => ({
        '@type': 'SocialMediaPosting',
        'accountName': channel.title,
        'url': channel.url,
      }));

      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': 'CODA Apeldoorn',
        'logo': 'https://coda-apeldoorn.nl/assets/gfx/logo-coda-black.svg',
        'contactPoint': {
          '@type': 'ContactPoint',
          'email': 'mail@coda-apeldoorn.nl',
          'contactType': 'customer service',
        },
        'sameAs': channels.map(c => c.url),
      };
    });
  },

  museum(data: Signal<SocialChannel[]>) {
    return computed(() => {
      const channels = data().map(channel => ({
        '@type': 'SocialMediaPosting',
        'accountName': channel.title,
        'url': channel.url,
      }));

      return {
        '@context': 'https://schema.org',
        '@type': 'Museum',
        'name': 'CODA Apeldoorn',
        'logo': 'https://coda-apeldoorn.nl/assets/gfx/logo-coda-black.svg',
        'sameAs': channels.map(c => c.url),
      };
    });
  },
} satisfies { [key: string]: (data: Signal<any>) => Signal<any> };
